import clsx from "clsx";
import { FC, useState } from "react";
import { twMerge } from "tailwind-merge";

import { FullSectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { Menu, type MenuChangeEvent, MenuItem } from "mds/components/Menu";
import { Tooltip } from "mds/components/Tooltip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { BookCourseIcon, ChevronRightIcon, CircleSuccessGreenIcon } from "mds/icons";
import { cn } from "mds/utils/cn";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectCurrentCourseId, selectFullCoursesSorted } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { Link, useNavigate } from "utils/routing";
import { courseHomeUrl, useViewLevel } from "utils/urls";

/**
 * A dropdown list of courses.
 */
export const CourseSelect: FC<{
  className?: string;
  disabled?: boolean;
  disabledTooltipText?: string;
  kind?: "primary" | "secondary";
  openInNewTab?: boolean;
  truncateTitle?: boolean;
}> = ({
  className,
  disabled,
  disabledTooltipText,
  kind = "primary",
  openInNewTab,
  truncateTitle,
}) => {
  const navigate = useNavigate();
  const currentCourseId = useAppSelector(selectCurrentCourseId) || "";
  const courses = useListSelector(selectFullCoursesSorted);
  // Note: we may still need to display an archived course if it's the current one, but for
  // the dropdown we filter them out.
  const { isActive } = useIsArchived();
  const filteredCourses = courses.filter(isActive);
  const viewLevel = useViewLevel();
  const isHomePage = viewLevel.home;
  const isCourseMainPage = viewLevel.course;
  const isCoursePage = viewLevel.course || viewLevel.page;
  const currentCourseLabel = isHomePage
    ? t("main_header.select_course")
    : courses.find((course) => course.id === currentCourseId)?.full_title;
  const hasCoursesToSelect = filteredCourses.length > 0;

  const [isMenuButtonHovered, setIsMenuButtonHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const buttonClasses = twMerge(
    "gap-1",
    kind === "secondary" && "!h-[32px] !max-h-[380px]",
    kind === "secondary" && !disabled && "!font-semibold",
    className,
  );

  const courseTitleTooltipText = disabled ? disabledTooltipText : "";

  const MenuButton = (
    <div>
      <Tooltip content={courseTitleTooltipText}>
        <Button
          className={buttonClasses}
          disabled={disabled}
          eventName="Open My Courses menu"
          kind="headerNav"
          size={kind === "primary" ? "s" : "m"}
          onMouseEnter={() => {
            setIsMenuButtonHovered(true);
          }}
          onMouseLeave={() => {
            setIsMenuButtonHovered(false);
          }}
        >
          {t("main_header.select_course")}
          <ChevronRightIcon
            className={clsx(
              "transition-transform duration-200 ease-in-out",
              (!isCoursePage || isMenuButtonHovered || isMenuOpen || kind === "secondary") &&
                "rotate-90",
            )}
          />
        </Button>
      </Tooltip>
    </div>
  );

  return (
    <div className={clsx("flex gap-2", kind === "secondary" && "flex-col")}>
      {hasCoursesToSelect && (
        <Menu
          align="start"
          menuButton={MenuButton}
          menuClassName="max-h-600"
          onMenuChange={(e: MenuChangeEvent) => {
            setIsMenuOpen(e.open);
          }}
        >
          {filteredCourses.map((course) => (
            <CourseMenuItem course={course} key={course.id} openInNewTab={openInNewTab} />
          ))}
        </Menu>
      )}
      {isCourseMainPage ? (
        <span
          className={cn(
            "flex h-[32px] items-center p-2 text-sm font-medium text-black-tint-20 group-data-[dark]/themable:text-black-tint-70",
            truncateTitle ? "max-w-l truncate" : "",
          )}
        >
          {currentCourseLabel}
        </span>
      ) : (
        isCoursePage && (
          <div>
            <Tooltip content={courseTitleTooltipText}>
              <Button
                className={buttonClasses}
                disabled={disabled}
                eventName="Go to Course Overview"
                kind="headerNav"
                size={kind === "primary" ? "s" : "m"}
                onClick={() => {
                  navigate(courseHomeUrl(currentCourseId));
                }}
              >
                <span className={truncateTitle ? "max-w-l truncate" : ""}>
                  {currentCourseLabel}
                </span>
              </Button>
            </Tooltip>
          </div>
        )
      )}
    </div>
  );
};

type CourseMenuItemProps = {
  course: FullSectionType;
  openInNewTab?: boolean;
};

const CourseMenuItem: FC<CourseMenuItemProps> = ({ course, openInNewTab }) => {
  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const checked = course.id === currentCourseId;
  const isMobile = !useIsMdOrLarger();
  const { canAuthorCourse } = useCapabilities();

  return (
    <Link target={openInNewTab ? "_blank" : undefined} to={courseHomeUrl(course.id)}>
      <MenuItem
        checked={checked}
        className={clsx("flex items-center justify-between gap-8", {
          selected: checked,
        })}
        eventName="Course Selected"
        eventProps={{ courseTitle: course.title }}
        value={course}
        allowPropagation
      >
        <div className="flex items-center gap-2">
          <BookCourseIcon />
          <div
            className={clsx("truncate", isMobile ? "max-w-xl" : "max-w-xxl")}
            title={course.full_title}
          >
            {course.full_title}
          </div>
        </div>

        {course.lms_section_id && canAuthorCourse && (
          <div className="flex items-center gap-1" title={course.full_title}>
            <CircleSuccessGreenIcon />
            <span>{t("main_header.course_linked")}</span>
          </div>
        )}
      </MenuItem>
    </Link>
  );
};
